import { ensureMaxWidthSetting } from "@library/shared";
import { MaxWidthSettingEnum } from "@library/types";
import {
  type ITimelineSettings,
  type ITimelineSpecificSettings,
  TimelineContentWrappingEnum,
  TimelineMarkerEnum,
  TimelinePositionEnum,
  TimelineSetContainerEnum,
  TimelineStyleEnum,
  TimelineTypeEnum,
} from "../types";

export const ensureTimelineComboSettings = (
  comboSettings: Record<string, unknown> | null = {},
  isMobile?: boolean,
  isPdf?: boolean,
): ITimelineSettings => {
  const {
    timelineType,
    timelineMarker,
    timelinePosition,
    timelineStyle,
    timelineSetContainer,
    timelineSetsPerSlide,
    timelineColor,
    timelineContentWrapping,
  } = comboSettings as Partial<ITimelineSettings>;

  const baseSettings = {
    horizontalAlignment: "center",
    verticalAlignment: "center",
    minHeight: "auto",
    isCustomHeight: false,
    maxWidth: ensureMaxWidthSetting(
      comboSettings?.maxWidth,
      MaxWidthSettingEnum.medium,
    ),
    isCustomWidth: false,
    ...comboSettings,
    isAnimationActive: false, // timeline is always aniamted with scroll
    name: "VTimeline",
  };

  // ====== Horizontal ====== \\
  /* On mobile, horizontal timelines render as a tabs-style swiper (one item
     at a time with a glimpse of the next), so we keep the Horizontal type
     instead of falling through to Vertical. */
  if (timelineType === TimelineTypeEnum.Horizontal) {
    const settings: ITimelineSpecificSettings = {
      timelineType: TimelineTypeEnum.Horizontal,
      timelineStyle: TimelineStyleEnum.Line,
      timelineSetContainer: TimelineSetContainerEnum.Full,
      timelineSetsPerSlide: timelineSetsPerSlide ?? "auto",
      timelineColor: timelineColor || "var(--theme-color-1)",
      //
      timelinePosition: TimelinePositionEnum.Top,
      timelineMarker: TimelineMarkerEnum.Bullets,
    };

    if (timelinePosition === TimelinePositionEnum.Bottom) {
      settings.timelinePosition = TimelinePositionEnum.Bottom;
    }
    if (timelinePosition === TimelinePositionEnum.Middle) {
      settings.timelinePosition = TimelinePositionEnum.Middle;
    }

    settings.timelineMarker = timelineMarker ?? TimelineMarkerEnum.Bullets;
    if (timelineStyle === TimelineStyleEnum.Path) {
      settings.timelineStyle = TimelineStyleEnum.Path;
    }

    // Mobile always renders horizontal as a Carousel (Swiper handles
    // horizontal overflow); PDF always renders as NewLine (no swiper,
    // all items must be visible in a static layout).
    if (isMobile) {
      settings.timelineContentWrapping = TimelineContentWrappingEnum.Carousel;
    } else if (isPdf) {
      settings.timelineContentWrapping = TimelineContentWrappingEnum.NewLine;
    } else {
      settings.timelineContentWrapping =
        timelineContentWrapping ?? TimelineContentWrappingEnum.Carousel;
    }

    const placement = (comboSettings as Record<string, unknown>)
      .timelineContentPlacement;
    const markerShape = (comboSettings as Record<string, unknown>)
      .timelineMarkerShape;

    // Both the split-middle layout AND the wide-chevron-middle layout
    // pin a track absolutely via splitMiddleRef + measured pos-0 / pos-1
    // heights. PDF capture often fires before ResizeObserver settles,
    // and mobile swiper-slides don't give the wrapper the height the
    // measurements need. Degrade those middle-positioned variants to
    // top so the track sits above the content stack instead of crossing
    // it / clipping the chevron.
    if (
      (isPdf || isMobile) &&
      settings.timelinePosition === "middle" &&
      (placement === "split" || markerShape === "arrow-big")
    ) {
      settings.timelinePosition = TimelinePositionEnum.Top;
    }

    // Mobile renders each item in a swiper-slide (one at a time); the
    // alternating above/track/below row layout and the snake-order
    // boustrophedon both need a shared parent grid the swiper doesn't
    // provide. Flatten them to per-item rendering on mobile.
    if (isMobile) {
      const overrides = settings as unknown as Record<string, unknown>;
      if (placement === "alternating") {
        overrides.timelineContentPlacement = "default";
      }
      overrides.timelineSnakeOrder = undefined;
    }

    return {
      ...baseSettings,
      ...settings,
    };
  }
  // ======================== \\

  // ======= Vertical ======= \\
  const settings: ITimelineSpecificSettings = {
    timelineType: TimelineTypeEnum.Vertical,
    timelineSetsPerSlide: "auto",
    timelineColor: timelineColor || "var(--theme-color-1)",
    //
    timelineStyle: TimelineStyleEnum.Line,
    timelineSetContainer: TimelineSetContainerEnum.Full,
    timelinePosition: TimelinePositionEnum.Center,
    timelineMarker: TimelineMarkerEnum.Bullets,
  };

  if (
    timelinePosition === TimelinePositionEnum.Right ||
    timelinePosition === TimelinePositionEnum.Left
  ) {
    settings.timelinePosition = timelinePosition;
  }
  if (timelinePosition === TimelinePositionEnum.Middle) {
    settings.timelinePosition = TimelinePositionEnum.Center;
  }

  if (timelineSetContainer === TimelineSetContainerEnum.SideBySide) {
    settings.timelineSetContainer = TimelineSetContainerEnum.SideBySide;
  }

  if (
    timelineMarker === TimelineMarkerEnum.Numbers ||
    timelineMarker === TimelineMarkerEnum.Arrows ||
    timelineMarker === TimelineMarkerEnum.CustomText
  ) {
    settings.timelineMarker = timelineMarker;
  }

  if (timelineStyle === TimelineStyleEnum.Path) {
    settings.timelineStyle = TimelineStyleEnum.Path;
    settings.timelinePosition = TimelinePositionEnum.Center;
  }

  if (isMobile) {
    settings.timelinePosition = TimelinePositionEnum.Left;
    if (timelinePosition === TimelinePositionEnum.Right) {
      settings.timelinePosition = TimelinePositionEnum.Right;
    }

    settings.timelineStyle = TimelineStyleEnum.Line;
    if (timelineStyle === TimelineStyleEnum.Path) {
      settings.timelinePosition = TimelinePositionEnum.Left;
    }
  }

  settings.timelineContentWrapping =
    timelineContentWrapping ?? TimelineContentWrappingEnum.NewLine;

  return {
    ...baseSettings,
    ...settings,
  };
};
